import { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box, Dialog, DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import { CloseOutlined, KeyboardArrowDownOutlined } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import {
  dialogHeaderStyles,
  menuProps,
  textFieldSx
} from '../../../../views/Common/components/Styles/InputStyles';

import CommonDialogActions from '../../../../views/Common/components/CommonDialogActions';

const initialForm = {
  lastName: '',
  firstName: '',
  middleName: '',
  dateOfBirth: '',
  gender: '',
  location: '',
  mobileNumber: '',
  emailAddress: ''
};

const PatientAddDialog = ({ open, onClose }) => {
  const [form, setForm] = useState(initialForm);
  const [dob, setDob] = useState(null);

  const handleChange = field => event => {
    const { value } = event.target;

    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.emailAddress);

  const isMobileValid = /^[0-9]{10}$/.test(form.mobileNumber);

  const isFormValid =
    form.lastName.trim() &&
    form.firstName.trim() &&
    form.dateOfBirth &&
    form.gender &&
    form.location &&
    isMobileValid &&
    isEmailValid;

  const handleClose = () => {
    setForm(initialForm);
    setDob(null);
    onClose();
  };

  const handleAddPatient = () => {
    if (!isFormValid) return;
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: {
            borderRadius: '8px'
          }
        }}>
        <DialogTitle
          sx={dialogHeaderStyles.dialogheader}>
          <Box>
            <Typography sx={dialogHeaderStyles.title}>Patient</Typography>

            <Typography sx={dialogHeaderStyles.subtitle}>
              Create a new patient record.
            </Typography>
          </Box>

          <IconButton
            aria-label="Close"
            onClick={handleClose}
            sx={{ color: '#424242' }}>
            <CloseOutlined />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2, mt: 1 }}>
          <Stack spacing={1.4}>
            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
              Personal Information
            </Typography>

            <TextField
              fullWidth
              required
              label="Last Name"
              onChange={handleChange('lastName')}
              size="small"
              sx={textFieldSx}
              value={form.lastName}
              variant="outlined"
            />

            <TextField
              fullWidth
              required
              label="First Name"
              onChange={handleChange('firstName')}
              size="small"
              sx={textFieldSx}
              value={form.firstName}
              variant="outlined"
            />

            <TextField
              fullWidth
              label="Middle Name"
              onChange={handleChange('middleName')}
              size="small"
              sx={textFieldSx}
              value={form.middleName}
              variant="outlined"
            />
            <DatePicker
              format="MM/DD/YYYY"
              label="Date of Birth"
              value={dob}
              onChange={newValue => {
                setDob(newValue);
                setForm(prev => ({
                  ...prev,
                  dateOfBirth: newValue ? newValue.format('MM/DD/YYYY') : ''
                }));
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  size: 'small',
                  sx: textFieldSx
                }
              }}
            />
            <FormControl fullWidth required size="small" sx={textFieldSx}>
              <InputLabel id="gender-label">Gender</InputLabel>

              <Select
                IconComponent={KeyboardArrowDownOutlined}
                label="Gender"
                labelId="gender-label"
                MenuProps={menuProps}
                onChange={handleChange('gender')}
                value={form.gender}>
                <MenuItem value="">Select Gender</MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Others">Others</MenuItem>
              </Select>
            </FormControl>

            <Typography
              sx={{
                mt: 1,
                fontSize: 13,
                fontWeight: 500
              }}>
              Contact Information
            </Typography>

            <FormControl fullWidth required size="small" sx={textFieldSx}>
              <InputLabel id="location-label">Location</InputLabel>

              <Select
                IconComponent={KeyboardArrowDownOutlined}
                label="Location"
                labelId="location-label"
                MenuProps={menuProps}
                onChange={handleChange('location')}
                value={form.location}>
                <MenuItem value="">Select Location</MenuItem>

                <MenuItem value="Elcamino Medical Clinic 6510 Lonetree Blvd">
                  Elcamino Medical Clinic 6510 Lonetree Blvd
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              required
              error={form.mobileNumber.length > 0 && !isMobileValid}
              helperText={
                form.mobileNumber.length > 0 && !isMobileValid
                  ? 'Enter a valid 10-digit mobile number'
                  : ''
              }
              inputProps={{
                inputMode: 'numeric',
                maxLength: 10
              }}
              label="Mobile Number"
              onChange={event => {
                const value = event.target.value.replace(/\D/g, '');

                setForm(prev => ({
                  ...prev,
                  mobileNumber: value
                }));
              }}
              size="small"
              sx={textFieldSx}
              value={form.mobileNumber}
              variant="outlined"
            />

            <TextField
              fullWidth
              required
              error={form.emailAddress.length > 0 && !isEmailValid}
              helperText={
                form.emailAddress.length > 0 && !isEmailValid
                  ? 'Enter a valid email address'
                  : ''
              }
              label="Email Address"
              onChange={handleChange('emailAddress')}
              size="small"
              sx={textFieldSx}
              type="email"
              value={form.emailAddress}
              variant="outlined"
            />
          </Stack>
        </DialogContent>

        <CommonDialogActions
          onCancel={handleClose}
          onSubmit={handleAddPatient}
          submitText="Add Patient"
          disabled={!isFormValid}
        />
      </Dialog>
    </LocalizationProvider>
  );
};

PatientAddDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired
};

export default PatientAddDialog;
